import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import moment from 'moment'
import 'moment/locale/ar'
import 'moment/locale/es'
import 'moment/locale/fr'
import 'moment/locale/pl'
import 'moment/locale/fil'
import 'moment/locale/vi'
import 'moment/locale/zh-cn'

import ar from './locales/ar.json'
import en from './locales/en.json'
import es from './locales/es.json'
import fr from './locales/fr.json'
import pl from './locales/pl.json'
import tl from './locales/tl.json'
import vi from './locales/vi.json'
import zh from './locales/zh.json'
import { setStreamChatLanguage } from './streamChatI18n'

// Map our i18next codes to the moment locale names installed above.
const MOMENT_LOCALES: Record<string, string> = {
  ar: 'ar',
  en: 'en',
  es: 'es',
  fr: 'fr',
  pl: 'pl',
  tl: 'fil', // moment ships Filipino as 'fil'
  vi: 'vi',
  zh: 'zh-cn',
}

// moment's Arabic locale rewrites every digit to Arabic-Indic in `postformat`, so
// `format('YYYY-MM-DD')` returns ٢٠٢٦-٠٩-٢٩. That substitution is not limited to
// display: it also hits the machine-readable strings we hand to AppSync (asOfDate,
// the calendar's timeMin/timeMax), and AWSDate/AWSDateTime reject non-ASCII digits,
// which left Arabic users with an empty calendar and blank dashboard metrics. Drop
// only the digit half of postformat and keep the Arabic comma, so dates still read
// naturally. `updateLocale` switches the active locale as a side effect, hence the
// save/restore around it.
const localeBeforeArabicPatch = moment.locale()
moment.updateLocale('ar', { postformat: (str: string) => str.replace(/,/g, '،') })
moment.locale(localeBeforeArabicPatch)

export const SUPPORTED_LANGUAGES = ['ar', 'en', 'es', 'fr', 'pl', 'tl', 'vi', 'zh'] as const
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number]

// Order here drives both the global picker (AppLanguagePicker) and the intake
// header (AppIntakeLanguageHeader) — keep alphabetical by English label.
export const LANGUAGE_OPTIONS: Array<{
  code: SupportedLanguage
  label: string
  nativeName: string
  flag: string
}> = [
  { code: 'ar', label: 'Arabic', nativeName: 'العربية', flag: '🇸🇦' },
  { code: 'zh', label: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
  { code: 'en', label: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'fr', label: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'pl', label: 'Polish', nativeName: 'Polski', flag: '🇵🇱' },
  { code: 'es', label: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'tl', label: 'Tagalog', nativeName: 'Tagalog', flag: '🇵🇭' },
  { code: 'vi', label: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳' },
]

export const STORAGE_KEY = 'gologic.preferredLanguage'

export const normalizeLanguage = (lang?: string | null): SupportedLanguage => {
  if (!lang) return 'en'
  const lower = lang.toLowerCase().split('-')[0]
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(lower)
    ? (lower as SupportedLanguage)
    : 'en'
}

// Arabic is the only right-to-left language we ship.
const RTL_LANGUAGES: readonly SupportedLanguage[] = ['ar']

/**
 * Single source of truth for text direction. Anything that has to branch on
 * direction — <html dir>, react-big-calendar's `rtl` prop, mirrored icons —
 * should ask here rather than comparing against 'ar' itself.
 */
export const isRtlLanguage = (lang?: string | null): boolean =>
  RTL_LANGUAGES.includes(normalizeLanguage(lang))

/**
 * Keeps <html lang> and <html dir> in step with the active language. Without
 * this the document stayed lang="en" with no dir in every locale, so assistive
 * tech and browser translation mis-read the page and Arabic rendered
 * left-to-right despite the text being translated.
 */
const applyDocumentLanguage = (normalized: SupportedLanguage) => {
  if (typeof document === 'undefined') return
  document.documentElement.lang = normalized
  document.documentElement.dir = isRtlLanguage(normalized) ? 'rtl' : 'ltr'
}

i18n.on('languageChanged', (lng) => {
  const normalized = normalizeLanguage(lng)
  moment.locale(MOMENT_LOCALES[normalized] || 'en')
  setStreamChatLanguage(normalized)
  applyDocumentLanguage(normalized)
})

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ar: { translation: ar },
      en: { translation: en },
      es: { translation: es },
      fr: { translation: fr },
      pl: { translation: pl },
      tl: { translation: tl },
      vi: { translation: vi },
      zh: { translation: zh },
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: STORAGE_KEY,
      caches: ['localStorage'],
    },
    returnNull: false,
  })

export default i18n
